import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useWallets, useSignTransaction } from '@privy-io/react-auth/solana';

import {
  useBroadcastTransactionMutation,
  useLazyGetAccountCreationInstructionsQuery,
  useLazyGetUnwrapSolInstructionsQuery,
  useSponsorTransactionMutation,
} from '../redux/api/authorized/sponsor';
import { Connection, PublicKey, VersionedTransaction } from '@solana/web3.js';
import { getAssociatedTokenAddress, TOKEN_PROGRAM_ID } from '@solana/spl-token';
import { useSelector, useDispatch, useStore } from 'react-redux';
import {
  usePrepareTransferMutation,
  useGetTokenBalancesQuery,
  useLazyGetTokenBalancesQuery,
} from '../redux/api/authorized/tokens';
import { useLazySearchTokensQuery } from '../redux/api/public/tokens';
import { updateWalletBalances } from '../redux/slices/walletSlice';
import {
  requestRefresh,
  setRefreshing,
  setLastRefreshTime,
  selectIsRefreshing,
  selectLastRefreshTime,
} from '../redux/slices/walletSlice';
import {
  selectTokensWithBalance,
  upsertTokens,
} from '../redux/slices/tokenSlice';
import { toast } from 'react-toastify';
import { useLazyGetTokenPriceQuery } from '../redux/api/public/tokens';
import { usePlaceTransactionAttemptMutation } from '../redux/api/authorized/transactionAttempts';
import {
  usePrepareGiftSwapMutation,
  useBroadcastGiftSwapMutation,
  usePrepareTransactionMutation,
  useBroadcastAndTrackMutation,
} from '../redux/api/authorized/gifts';
import { selectCombinedTokenBalances } from '../redux/slices/walletSlice';
import {
  useBroadcastPackTransactionMutation,
  usePreparePackTransactionMutation,
} from '../redux/api/authorized/packs';
import { hapticsImpactLight } from '../common/haptic';
import retry from '../common/retry';
import {
  useLazyCreateOrderQuery,
  useLazyExecuteOrderQuery,
} from '../redux/api/authorized/ultra';

export const SOLANA_NETWORK = import.meta.env.VITE_SOLANA_RPC_URL;
const connection = new Connection(SOLANA_NETWORK, 'confirmed');
const WRAPPED_SOL_MINT = 'So11111111111111111111111111111111111111112';

const DEFAULT_SLIPPAGE = import.meta.env.VITE_DEFAULT_SLIPPAGE;

function base64ToUint8Array(base64) {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
}

function serializeTransactionToBase64(transaction) {
  return btoa(
    Array.from(transaction)
      .map((byte) => String.fromCharCode(byte))
      .join(''),
  );
}

export function toTokenAmount(value, decimals) {
  if (value === null || value === undefined) {
    return '0';
  }

  const [whole, fraction = ''] = String(value).split('.');
  const paddedFraction = fraction.padEnd(decimals, '0').slice(0, decimals);

  const amount = BigInt(whole + paddedFraction);
  return amount.toString();
}

export function fromTokenAmount(value, decimals) {
  if (!value) return 0;

  const valueStr = String(value);
  const paddedValue = valueStr.padStart(decimals + 1, '0');

  const decimalPos = paddedValue.length - decimals;
  const result =
    paddedValue.substring(0, decimalPos) +
    (decimals > 0 ? '.' + paddedValue.substring(decimalPos) : '');

  return parseFloat(result);
}

const walletsChanged = (prev, curr) => {
  if (prev.length !== curr.length) return true;
  return prev.some((wallet, index) => wallet.address !== curr[index]?.address);
};

export const useSolana = () => {
  const { wallets: privyWallets } = useWallets();
  const dispatch = useDispatch();
  const store = useStore();
  const { signTransaction } = useSignTransaction();

  const tokensWithBalance = useSelector((state) =>
    selectTokensWithBalance(state),
  );

  // Get combined token balances from both wallet types
  const combinedTokenBalances = useSelector(selectCombinedTokenBalances);

  const [triggerCreateOrder] = useLazyCreateOrderQuery();
  const [triggerExecuteOrder] = useLazyExecuteOrderQuery();
  const [sponsorTransaction] = useSponsorTransactionMutation();
  const [broadcastTransaction] = useBroadcastTransactionMutation();
  const [placeTransactionAttempt] = usePlaceTransactionAttemptMutation();
  const [triggerGetTokenPriceQuery] = useLazyGetTokenPriceQuery();
  const [prepareTransfer] = usePrepareTransferMutation();
  const [prepareGiftSwap] = usePrepareGiftSwapMutation();
  const [broadcastGiftSwap] = useBroadcastGiftSwapMutation();
  const [prepareTransaction] = usePrepareTransactionMutation();
  const [broadcastAndTrack] = useBroadcastAndTrackMutation();
  const [triggerSearchTokens] = useLazySearchTokensQuery();
  const [preparePackTransaction] = usePreparePackTransactionMutation();
  const [broadcastPackTransaction] = useBroadcastPackTransactionMutation();
  const [triggerGetAccountCreationInstructions] =
    useLazyGetAccountCreationInstructionsQuery();
  const [triggerGetUnwrapSolInstructions] =
    useLazyGetUnwrapSolInstructionsQuery();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [walletsReady, setWalletsReady] = useState(false);

  // Memoize wallet objects to prevent unnecessary recreation
  const embeddedWallet = useMemo(
    () =>
      privyWallets?.find((wallet) => wallet.standardWallet.isPrivyWallet) ||
      null,
    [privyWallets],
  );

  const externalWallet = useMemo(
    () =>
      privyWallets?.find((wallet) => !wallet.standardWallet.isPrivyWallet) ||
      null,
    [privyWallets],
  );

  // Update walletsReady when wallets change
  useEffect(() => {
    if (privyWallets?.length > 0) {
      setWalletsReady(true);
    } else {
      setWalletsReady(false);
    }
  }, [privyWallets]);

  // Compute primary wallet (embedded takes precedence)
  const primaryWallet = embeddedWallet || externalWallet;

  // Memoize the wallets array to prevent unnecessary re-renders
  const wallets = useMemo(() => {
    return [primaryWallet].filter(Boolean);
  }, [primaryWallet]);

  // Get embedded wallet balances
  const { data: embeddedTokenBalances } = useGetTokenBalancesQuery(
    {
      walletAddress: embeddedWallet?.address,
      walletType: 'embedded',
    },
    {
      skip: !embeddedWallet,
    },
  );

  const [getTokenBalances] = useLazyGetTokenBalancesQuery();

  const refetchEmbeddedBalances = () => {
    return getTokenBalances({
      walletAddress: embeddedWallet?.address,
      walletType: 'embedded',
    });
  };

  // Get external wallet balances
  const { data: externalTokenBalances } = useGetTokenBalancesQuery(
    {
      walletAddress: externalWallet?.address,
      walletType: 'external',
    },
    {
      skip: !externalWallet,
    },
  );

  const refetchExternalBalances = () => {
    return getTokenBalances({
      walletAddress: externalWallet?.address,
      walletType: 'external',
    });
  };

  const getPendingTransactions = async (connection, signatures) => {
    try {
      const statuses = await connection.getSignatureStatuses(signatures, {
        searchTransactionHistory: true,
      });
      return statuses.value.filter(
        (status) =>
          !status?.confirmationStatus ||
          status?.confirmationStatus === 'processed',
      );
    } catch (error) {
      console.error('Error fetching pending transactions:', error);
      return [];
    }
  };

  const getWalletPendingTransactions = async () => {
    try {
      if (!primaryWallet) {
        return [];
      }
      const publicKey = new PublicKey(primaryWallet.address);
      const connection = new Connection(SOLANA_NETWORK, 'confirmed');
      const transactions = await connection.getConfirmedSignaturesForAddress2(
        publicKey,
        { limit: 10 },
      );
      const pendingTransactions = await getPendingTransactions(
        connection,
        transactions.map((tx) => tx.signature),
      );
      return pendingTransactions;
    } catch (error) {
      console.error("Error fetching wallet's pending transactions:", error);
      return [];
    }
  };

  const signAndSerializeTransaction = useCallback(
    async (serializedTransaction) => {
      if (!primaryWallet) {
        throw new Error('No wallet connected');
      }

      const bytes = base64ToUint8Array(serializedTransaction);

      const tx = VersionedTransaction.deserialize(bytes);

      const serializedMessage = Buffer.from(tx.message.serialize()).toString(
        'base64',
      );

      const { signedMessage, signature } = await primaryWallet.signMessage({
        message: tx.message.serialize(),
      });

      tx.addSignature(new PublicKey(primaryWallet.address), signature);

      const base64String = serializeTransactionToBase64(tx.serialize());

      return base64String;
    },
    [primaryWallet],
  );

  const signAndSerializeTransactionWithWallet = useCallback(
    async (serializedTransaction, wallet) => {
      if (!wallet) {
        throw new Error('No wallet provided');
      }

      const bytes = base64ToUint8Array(serializedTransaction);

      const tx = VersionedTransaction.deserialize(bytes);
      // External wallets like Phantom use signTransaction
      const signedTx = await signTransaction({
        wallet,
        transaction: bytes,
      });

      const base64String = serializeTransactionToBase64(
        signedTx.signedTransaction,
      );

      return base64String;
    },
    [signTransaction],
  );

  const getTokenBalance = useCallback(
    async ({ tokenAddress, programId }) => {
      try {
        if (!primaryWallet) {
          return { balance: 0, uiBalanceString: '0' };
        }

        const userPublicKey = new PublicKey(primaryWallet.address);
        const mintPublicKey = new PublicKey(tokenAddress);

        const tokenATA = await getAssociatedTokenAddress(
          mintPublicKey,
          userPublicKey,
          false,
          new PublicKey(programId),
        );

        const accountInfo = await connection.getAccountInfo(tokenATA);

        if (!accountInfo) {
          return { balance: 0, uiBalanceString: '0' };
        }

        const tokenAccount = await connection.getTokenAccountBalance(tokenATA);

        return {
          balance: tokenAccount.value.uiAmount || 0,
          uiBalanceString: tokenAccount.value.uiAmountString || '0',
        };
      } catch (err) {
        console.error('Error in getTokenBalance:', err);
        return { balance: 0, uiBalanceString: '0' };
      }
    },
    [primaryWallet, connection],
  );

  // Process raw balance data and update Redux store
  const processTokenBalances = useCallback(
    async (dispatch) => {
      try {
        // Get raw balance data for both wallets
        const embeddedRawBalances = embeddedTokenBalances || [];
        const externalRawBalances = externalTokenBalances || [];

        // Combine all unique addresses
        const allAddresses = new Set();
        embeddedRawBalances.forEach((balance) =>
          allAddresses.add(balance.address),
        );
        externalRawBalances.forEach((balance) =>
          allAddresses.add(balance.address),
        );

        if (allAddresses.size === 0) return;

        // Fetch token metadata from backend for all addresses
        const addresses = Array.from(allAddresses);
        const tokenMetadataResponse = await triggerSearchTokens({
          addresses,
        }).unwrap();

        if (!tokenMetadataResponse) {
          throw new Error('Failed to fetch token metadata');
        }

        const tokenMetadata = tokenMetadataResponse.tokens || [];

        // Process embedded wallet balances
        const embeddedBalanceUpdates = embeddedRawBalances
          .map((rawBalance) => {
            const token = tokenMetadata.find(
              (t) => t.address === rawBalance.address,
            );
            if (!token) return null;

            const rawAmount = BigInt(rawBalance.rawAmount);
            const balance = Number(rawAmount) / Math.pow(10, token.decimals);

            return {
              network: 'solana',
              address: rawBalance.address,
              walletType: 'embedded',
              balance: balance,
              uiString: balance.toString(),
            };
          })
          .filter(Boolean);

        // Process external wallet balances
        const externalBalanceUpdates = externalRawBalances
          .map((rawBalance) => {
            const token = tokenMetadata.find(
              (t) => t.address === rawBalance.address,
            );
            if (!token) return null;

            const rawAmount = BigInt(rawBalance.rawAmount);
            const balance = Number(rawAmount) / Math.pow(10, token.decimals);

            return {
              network: 'solana',
              address: rawBalance.address,
              walletType: 'external',
              balance: balance,
              uiString: balance.toString(),
            };
          })
          .filter(Boolean);

        // Simple approach: replace all balances with new ones
        // This automatically removes balances that are no longer present
        const allBalanceUpdates = [
          ...embeddedBalanceUpdates,
          ...externalBalanceUpdates,
        ];

        // Use replaceWalletBalances to completely replace all balances
        // This will automatically remove any balances that are no longer present
        dispatch(updateWalletBalances(allBalanceUpdates));

        // Update token metadata in Redux
        if (tokenMetadata.length > 0) {
          const tokenMetadataToUpsert = tokenMetadata.map((token) => ({
            ...token, // Keep all original backend data
            network: token.network || 'solana', // Only add network if missing
          }));

          dispatch(upsertTokens({ tokens: tokenMetadataToUpsert }));
        }
      } catch (error) {
        console.error('Error processing token balances:', error);
      }
    },
    [embeddedTokenBalances, externalTokenBalances, triggerSearchTokens],
  );

  const refreshTokenBalances = useCallback(
    async (force = false) => {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      try {
        // Get current state directly from Redux instead of relying on hook state
        const currentState = store.getState();
        const isCurrentlyRefreshing = selectIsRefreshing(currentState);
        const lastRefresh = selectLastRefreshTime(currentState);

        if (isCurrentlyRefreshing) {
          return;
        }

        const now = Date.now();
        const COOLDOWN_MS = 5000;
        if (now - lastRefresh < COOLDOWN_MS && !force) {
          return;
        }

        // Immediately mark as refreshing to prevent duplicate calls
        dispatch(requestRefresh());
        setLoading(true);

        // Refetch both wallet balances
        if (embeddedWallet && refetchEmbeddedBalances) {
          await refetchEmbeddedBalances();
        }
        if (externalWallet && refetchExternalBalances) {
          await refetchExternalBalances();
        }

        // Process the raw balance data and fetch token metadata
        await processTokenBalances(dispatch);

        // Update last refresh time
        dispatch(setLastRefreshTime(now));
      } catch (error) {
        console.error('Error refreshing token balances:', error);
        setError(error);
      } finally {
        // Always reset refreshing state
        dispatch(setRefreshing(false));
        setLoading(false);
      }
    },
    [
      dispatch,
      processTokenBalances,
      embeddedWallet,
      externalWallet,
      refetchEmbeddedBalances,
      refetchExternalBalances,
    ],
  );

  // Automatically process balances when both wallet data are available
  useEffect(() => {
    // Only process when we have data from both wallets (or when wallets are not available)
    const hasEmbeddedData = embeddedWallet ? embeddedTokenBalances : true;
    const hasExternalData = externalWallet ? externalTokenBalances : true;

    if (hasEmbeddedData && hasExternalData) {
      processTokenBalances(dispatch);
    }
  }, [
    embeddedTokenBalances,
    externalTokenBalances,
    embeddedWallet,
    externalWallet,
    processTokenBalances,
    dispatch,
  ]);

  const waitForTokenAccountExists = useCallback(
    async ({ tokenAddress, walletAddress }) => {
      if (!walletAddress) {
        throw new Error('Wallet address required for account verification');
      }

      // Get the mint account to find the program ID
      const mintPublicKey = new PublicKey(tokenAddress);
      const mintAccount = await connection.getAccountInfo(mintPublicKey);

      if (!mintAccount) {
        throw new Error(`Mint account not found for ${tokenAddress}`);
      }

      const programId = mintAccount.owner;
      const userPublicKey = new PublicKey(walletAddress);

      // Calculate the associated token account address
      const tokenATA = await getAssociatedTokenAddress(
        mintPublicKey,
        userPublicKey,
        false,
        programId,
      );

      // Check if account exists with retries
      let attempts = 0;
      const maxAttempts = 5;
      const retryDelay = 500; // 500ms between retries

      while (attempts < maxAttempts) {
        const accountInfo = await connection.getAccountInfo(
          tokenATA,
          'confirmed',
        );

        if (accountInfo) {
          return true; // Account exists
        }

        attempts++;
        if (attempts < maxAttempts) {
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
        }
      }

      console.warn(
        `Token account verification timeout after ${maxAttempts} attempts for ${tokenAddress}`,
      );
      return false; // Account not found after retries
    },
    [connection],
  );

  const createNeededTokenAccounts = useCallback(
    async ({ tokenAddress, walletAddress = null }) => {
      const targetWalletAddress = walletAddress || primaryWallet?.address;

      if (!targetWalletAddress) {
        throw new Error(
          'No wallet address available for token account creation',
        );
      }

      const accountCreationResponse =
        await triggerGetAccountCreationInstructions({
          tokenAddress,
        }).unwrap();

      if (accountCreationResponse?.serializedTransaction) {
        const base64String = await signAndSerializeTransaction(
          accountCreationResponse.serializedTransaction,
        );
        const { transactionId } = await broadcastTransaction({
          serializedTransaction: base64String,
        }).unwrap();

        await confirmTransaction(connection, transactionId);

        // Verify the token account exists
        await waitForTokenAccountExists({
          tokenAddress,
          walletAddress: targetWalletAddress,
        });
      }
    },
    [
      triggerGetAccountCreationInstructions,
      signAndSerializeTransaction,
      broadcastTransaction,
      confirmTransaction,
      connection,
      waitForTokenAccountExists,
      primaryWallet,
    ],
  );

  const unwrapExistingWrappedSol = useCallback(async () => {
    if (!primaryWallet?.address) {
      return;
    }

    try {
      const ownerPublicKey = new PublicKey(primaryWallet.address);
      const wrappedSolMint = new PublicKey(WRAPPED_SOL_MINT);

      const wrappedSolAta = await getAssociatedTokenAddress(
        wrappedSolMint,
        ownerPublicKey,
        false,
        TOKEN_PROGRAM_ID,
      );

      const accountInfo = await connection.getAccountInfo(wrappedSolAta);
      if (!accountInfo) {
        return;
      }

      const balanceInfo =
        await connection.getTokenAccountBalance(wrappedSolAta);
      const rawAmount = BigInt(balanceInfo?.value?.amount || '0');

      console.log('unwrap: rawAmount', rawAmount);

      if (rawAmount === 0n) {
        return;
      }

      const instructionResponse =
        await triggerGetUnwrapSolInstructions().unwrap();
      const serializedUnwrapTransaction =
        instructionResponse?.serializedTransaction;

      if (!serializedUnwrapTransaction) {
        throw new Error('Failed to fetch unwrap SOL instructions');
      }

      let signedTransactionBase64 = await signAndSerializeTransactionWithWallet(
        serializedUnwrapTransaction,
        primaryWallet,
      );

      const { transactionId } = await broadcastTransaction({
        serializedTransaction: signedTransactionBase64,
      }).unwrap();

      await confirmTransaction(connection, transactionId);
      console.log('unwrap: finished');
      await refreshTokenBalances({ force: true });
    } catch (error) {
      console.error('Error unwrapping wrapped SOL:', error);
      throw error;
    }
  }, [
    connection,
    embeddedWallet,
    primaryWallet,
    signAndSerializeTransaction,
    signAndSerializeTransactionWithWallet,
    broadcastTransaction,
    triggerGetUnwrapSolInstructions,
    refreshTokenBalances,
  ]);

  const executeUltraSwap = useCallback(
    async ({ inputMint, outputMint, amount, decimals = 9 }) => {
      setLoading(true);
      setError(null);
      let toastId = null;
      let swapSignature = null;
      let swapResult = null;

      try {
        if (inputMint === WRAPPED_SOL_MINT) {
          await unwrapExistingWrappedSol();
        }

        await createNeededTokenAccounts({
          tokenAddress: outputMint,
          walletAddress: primaryWallet.address,
        });
        const tokenAmount =
          typeof amount === 'string' && !amount.includes('.')
            ? amount
            : toTokenAmount(amount, decimals);

        toastId = toast('Preparing transaction...', {
          isLoading: true,
          autoClose: false,
        });

        await retry(async () => {
          const order = await triggerCreateOrder({
            inputMint,
            outputMint,
            amount: tokenAmount,
          }).unwrap();

          toast.update(toastId, {
            render: 'Signing transaction...',
            isLoading: true,
            autoClose: false,
          });

          const { transaction, requestId } = order;
          const base64String = await signAndSerializeTransactionWithWallet(
            transaction,
            primaryWallet,
          );

          toast.update(toastId, {
            render: 'Broadcasting transaction...',
            isLoading: true,
            autoClose: false,
          });

          const executeResponse = await triggerExecuteOrder({
            userSignedTransaction: base64String,
            requestId,
          }).unwrap();

          swapSignature = executeResponse?.signature;
          swapResult = executeResponse;

          if (!swapSignature) {
            throw new Error('Swap did not return a transaction signature.');
          }

          toast.update(toastId, {
            render: 'Confirming transaction...',
            isLoading: true,
            autoClose: false,
          });

          await confirmTransaction(connection, swapSignature);
        });

        await refreshTokenBalances({ force: true });

        hapticsImpactLight();

        if (toastId) {
          toast.update(toastId, {
            render: 'Transaction confirmed!',
            type: 'success',
            isLoading: false,
            autoClose: 5000,
          });
        }

        return {
          signature: swapSignature,
          result: swapResult,
        };
      } catch (err) {
        const errorMessage = parseSolanaError(err);

        setError(errorMessage);

        if (toastId) {
          toast.update(toastId, {
            render: `Transaction failed: ${errorMessage}`,
            type: 'error',
            isLoading: false,
            autoClose: 5000,
          });
        }
        return null;
      } finally {
        setLoading(false);
      }
    },
    [
      triggerCreateOrder,
      triggerExecuteOrder,
      refreshTokenBalances,
      signAndSerializeTransactionWithWallet,
      primaryWallet,
      createNeededTokenAccounts,
      unwrapExistingWrappedSol,
    ],
  );

  const executeGiftSwap = useCallback(
    async ({ giftId, inputMint, inputAmount, slippageBps = 'auto' }) => {
      setLoading(true);
      setError(null);

      try {
        if (!embeddedWallet) {
          throw new Error('Embedded wallet required for gift transactions');
        }

        const swapResp = await prepareGiftSwap({
          giftId,
          inputMint,
          inputAmount,
          slippageBps,
        }).unwrap();

        const { serializedTransaction } = swapResp;
        const base64String = await signAndSerializeTransaction(
          serializedTransaction,
        );

        const { transactionId } = await broadcastGiftSwap({
          giftId,
          serializedTransaction: base64String,
        }).unwrap();

        await confirmTransaction(connection, transactionId);

        await refreshTokenBalances({ force: true });

        return transactionId;
      } catch (err) {
        const errorMessage = parseSolanaError(err);
        setError(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [
      refreshTokenBalances,
      signAndSerializeTransaction,
      connection,
      embeddedWallet,
      prepareGiftSwap,
      broadcastGiftSwap,
    ],
  );

  const executeGiftHTLCCreation = useCallback(
    async ({ giftId, outputMint }) => {
      setLoading(true);
      setError(null);

      try {
        if (!embeddedWallet) {
          throw new Error('Embedded wallet required for gift transactions');
        }

        let result = null;

        await retry(async () => {
          if (outputMint === WRAPPED_SOL_MINT) {
            // If we are doing a SOL gift, make sure we have a wsol account created.
            await createNeededTokenAccounts({ tokenAddress: outputMint });
          }

          const { serializedTransaction } =
            await prepareTransaction(giftId).unwrap();

          const base64String = await signAndSerializeTransaction(
            serializedTransaction,
          );

          result = await broadcastAndTrack({
            giftId,
            serializedTransaction: base64String,
          }).unwrap();

          if (result.transactionId) {
            await confirmTransaction(connection, result.transactionId);
          }
        });

        await refreshTokenBalances({ force: true });

        return result;
      } catch (err) {
        const errorMessage = parseSolanaError(err);
        setError(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [
      refreshTokenBalances,
      signAndSerializeTransaction,
      connection,
      embeddedWallet,
      prepareTransaction,
      broadcastAndTrack,
    ],
  );

  const executePackTransaction = useCallback(
    async ({ baseToken, baseTokenAmount, packType }) => {
      setLoading(true);
      setError(null);

      try {
        if (!embeddedWallet) {
          throw new Error('Embedded wallet required for pack transactions');
        }
        const { serializedTransaction } = await preparePackTransaction({
          baseToken,
          baseTokenAmount,
        }).unwrap();

        const base64String = await signAndSerializeTransaction(
          serializedTransaction,
        );

        const result = await broadcastPackTransaction({
          serializedTransaction: base64String,
          baseToken,
          baseTokenAmount,
          packType,
        }).unwrap();

        if (result.transactionId) {
          await confirmTransaction(connection, result.transactionId);
        }

        await refreshTokenBalances();

        return result?.tokens;
      } catch (err) {
        const errorMessage = parseSolanaError(err);
        setError(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [
      refreshTokenBalances,
      signAndSerializeTransaction,
      connection,
      embeddedWallet,
      preparePackTransaction,
      broadcastPackTransaction,
    ],
  );

  const sendUsdc = useCallback(
    async (amount, recipientAddress) => {
      setLoading(true);
      setError(null);
      let toastId = toast('Preparing transaction...', {
        isLoading: true,
        autoClose: false,
      });

      try {
        if (!amount || !recipientAddress) {
          throw new Error('Amount and recipient address are required');
        }

        if (!primaryWallet?.address) {
          throw new Error('No wallet connected');
        }

        let recipientPublicKey;
        try {
          recipientPublicKey = new PublicKey(recipientAddress);
        } catch (error) {
          throw new Error('Invalid recipient address');
        }

        const usdcMint = import.meta.env.VITE_SOLANA_USDC_MINT;

        toast.update(toastId, {
          render: 'Signing transaction...',
          isLoading: true,
          autoClose: false,
        });

        const { transaction: serializedTransaction } = await prepareTransfer({
          network: 'solana',
          token: usdcMint,
          sourceWallet: primaryWallet.address,
          destinationWallet: recipientAddress,
          amount,
        }).unwrap();

        const base64String = await signAndSerializeTransaction(
          serializedTransaction,
        );

        toast.update(toastId, {
          render: 'Broadcasting transaction...',
          isLoading: true,
          autoClose: false,
        });

        const { transactionId } = await broadcastTransaction({
          serializedTransaction: base64String,
        }).unwrap();

        toast.update(toastId, {
          render: 'Confirming transaction...',
          isLoading: true,
          autoClose: false,
        });

        await confirmTransaction(connection, transactionId);

        await refreshTokenBalances({ force: true });

        toast.update(toastId, {
          render: 'Transfer successful',
          type: 'success',
          isLoading: false,
          autoClose: 5000,
        });

        return {
          success: true,
          message: 'Transaction completed successfully',
          transactionId,
        };
      } catch (error) {
        console.error('Send USDC error:', error);
        setError(error);
        toast.update(toastId, {
          render: error.message || 'There was an error with your transfer',
          type: 'error',
          isLoading: false,
          autoClose: 5000,
        });
        return { error };
      } finally {
        setLoading(false);
      }
    },
    [
      connection,
      sponsorTransaction,
      broadcastTransaction,
      refreshTokenBalances,
      signAndSerializeTransaction,
    ],
  );

  const transferToken = useCallback(
    async (tokenSymbol, amount, fromWallet, toWalletAddress) => {
      setLoading(true);
      setError(null);
      let toastId = toast('Preparing transfer...', {
        isLoading: true,
        autoClose: false,
      });

      try {
        if (!fromWallet?.address || !toWalletAddress) {
          throw new Error(
            'Both source and destination wallets must be connected',
          );
        }

        if (!amount || amount <= 0) {
          throw new Error('Invalid transfer amount');
        }

        // Get token details from tokensWithBalance
        const token = tokensWithBalance.find((t) => t.symbol === tokenSymbol);
        if (!token) {
          throw new Error(`Token ${tokenSymbol} not found`);
        }

        if (token.address === WRAPPED_SOL_MINT || tokenSymbol === 'SOL') {
          await unwrapExistingWrappedSol();
        }

        toast.update(toastId, {
          render: 'Signing transfer transaction...',
          isLoading: true,
          autoClose: false,
        });

        // Prepare the transfer
        await retry(async () => {
          const { transaction: serializedTransaction } = await prepareTransfer({
            network: 'solana',
            token: token.address,
            sourceWallet: fromWallet.address,
            destinationWallet: toWalletAddress,
            amount: toTokenAmount(amount, token.decimals),
          }).unwrap();

          const base64String = await signAndSerializeTransactionWithWallet(
            serializedTransaction,
            fromWallet,
          );

          toast.update(toastId, {
            render: 'Broadcasting transfer...',
            isLoading: true,
            autoClose: false,
          });

          const { transactionId } = await broadcastTransaction({
            serializedTransaction: base64String,
          }).unwrap();

          toast.update(toastId, {
            render: 'Confirming transfer...',
            isLoading: true,
            autoClose: false,
          });

          await confirmTransaction(connection, transactionId);
        });

        await refreshTokenBalances({ force: true });

        toast.update(toastId, {
          render: 'Transfer completed successfully',
          type: 'success',
          isLoading: false,
          autoClose: 5000,
        });

        return {
          success: true,
          message: 'Transfer completed successfully',
        };
      } catch (error) {
        console.error('Transfer token error:', error);
        setError(error);
        toast.update(toastId, {
          render: error.message || 'There was an error with the transfer',
          type: 'error',
          isLoading: false,
          autoClose: 5000,
        });
        return { error };
      } finally {
        setLoading(false);
      }
    },
    [
      connection,
      tokensWithBalance,
      prepareTransfer,
      broadcastTransaction,
      refreshTokenBalances,
      signAndSerializeTransactionWithWallet,
      toTokenAmount,
      unwrapExistingWrappedSol,
    ],
  );

  const getTokenPrice = useCallback(
    async (token) => {
      try {
        if (!token?.address) {
          return null;
        }

        const priceData = await triggerGetTokenPriceQuery(
          token.address,
        ).unwrap();

        if (!priceData) {
          return null;
        }

        return priceData?.priceUSDC;
      } catch (err) {
        console.error('Error getting token price:', err);
        return null;
      }
    },
    [triggerGetTokenPriceQuery],
  );

  const calculateTokenAmount = useCallback(
    (usdAmount, tokenPriceUSD, decimals = 2) => {
      if (!tokenPriceUSD || !usdAmount) return '';
      return (parseFloat(usdAmount) / tokenPriceUSD).toFixed(decimals);
    },
    [],
  );

  const parseSolanaError = (err) => {
    // Extract the actual error content from various wrapper formats
    const extractErrorContent = (error) => {
      if (!error) return null;

      // Handle direct error object
      if (error?.InstructionError) return error;

      // Handle string errors
      if (typeof error === 'string') {
        // Extract JSON from "Transaction simulation failed:" or "Transaction failed:" prefixes
        const match = error.match(
          /(?:Transaction (?:simulation )?failed: )(.+)/,
        );
        if (match) {
          try {
            return JSON.parse(match[1]);
          } catch (e) {
            return error;
          }
        }
        return error;
      }

      // Handle error objects with nested error properties
      if (
        err?.data?.details === 'Swap between Pump.fun tokens is not supported'
      ) {
        return 'Swap between Pump.fun tokens is not supported.';
      }
      if (error?.error) return extractErrorContent(error.error);
      if (error?.data?.error) return extractErrorContent(error.data.error);
      if (error?.data?.details) return extractErrorContent(error.data.details);
      if (error?.message) return extractErrorContent(error.message);

      return null;
    };

    // Parse the actual error content
    const parseErrorContent = (content) => {
      if (!content) return null;

      // Handle InstructionError format
      if (content?.InstructionError) {
        const [index, customError] = content.InstructionError;
        // Handle insufficient funds errors from different programs
        if (customError?.Custom === 1 || customError?.Custom === 40) {
          return 'Insufficient funds. Please check your balance and try again.';
        }
        if (customError?.Custom === 6001) {
          return 'Adjust slippage and try again';
        }
        return `Transaction failed: Instruction ${index} failed`;
      }

      // Handle string errors
      if (typeof content === 'string') {
        if (content.includes('not tradable')) {
          return 'Token is not live yet - wait and try again.';
        }
        if (content.includes('encoded message is too large')) {
          return 'Transaction is too large. Please try a smaller amount.';
        }
        return content;
      }

      return null;
    };

    // Extract and parse the error
    const errorContent = extractErrorContent(err);
    const parsedError = parseErrorContent(errorContent);

    // Handle special cases
    if (!parsedError) {
      if (err?.status === 429 || err?.originalStatus === 429) {
        return 'Too many attempts. Wait a moment and try again.';
      }
      if (err?.status === 403 || err?.originalStatus === 403) {
        return 'Too many requests. Wait a moment and try again.';
      }
      return err?.data?.details?.[0] || err?.message || 'Unknown error';
    }

    return parsedError;
  };

  return {
    // Individual wallet properties
    embeddedWallet,
    externalWallet,
    primaryWallet,

    // Backwards compatibility, ensuring no null value if primaryWallet is null
    wallets,

    walletsReady,
    loading,
    error,
    tokenBalances: combinedTokenBalances,
    embeddedTokenBalances,
    externalTokenBalances,

    getTokenBalance,
    refreshTokenBalances,
    processTokenBalances,

    getWalletPendingTransactions,

    executeGiftSwap,
    executeGiftHTLCCreation,
    sendUsdc,
    transferToken,
    signAndSerializeTransaction,
    signAndSerializeTransactionWithWallet,
    broadcastTransaction,
    toTokenAmount,
    fromTokenAmount,
    getTokenPrice,
    calculateTokenAmount,
    parseSolanaError,
    executePackTransaction,
    executeUltraSwap,
  };
};

const confirmTransaction = async (connection, transactionId) => {
  try {
    return new Promise((resolve, reject) => {
      const subscriptionId = connection.onSignature(
        transactionId,
        (result) => {
          if (result.err) {
            reject(result.err);
          } else {
            resolve(result);
          }
          connection.removeSignatureListener(subscriptionId);
        },
        'confirmed',
      );

      setTimeout(() => {
        connection.removeSignatureListener(subscriptionId);
        reject(new Error('Transaction confirmation timeout'));
      }, 30000);
    });
  } catch (error) {
    console.error(
      'WebSocket confirmation failed, falling back to polling:',
      error,
    );

    // Fallback to polling
    try {
      const latestBlockhash = await connection.getLatestBlockhash('confirmed');
      const confirmation = await connection.confirmTransaction(
        {
          signature: transactionId,
          blockhash: latestBlockhash.blockhash,
          lastValidBlockHeight: latestBlockhash.lastValidBlockHeight,
        },
        'confirmed',
      );

      if (confirmation.value.err) {
        throw confirmation.value.err;
      }

      return confirmation;
    } catch (pollingError) {
      console.error('Polling confirmation also failed:', pollingError);
      throw pollingError;
    }
  }
};
